<main>
  <loading-overlay [isLoading]="isLoading() > 0" />
  <p-confirmDialog />

  <header>
    <div class="row-start-center gap-3">
      <h1>{{ funding()?.number || ('CashFunding_Title_New' | translateWF: 'New funding') }}</h1>
      @if (funding(); as f) {
        <p-tag [severity]="statusSeverity(f.status)" [value]="statusLabel(f.status) | translateWF: statusLabel(f.status)" />
      }
    </div>
    <div class="buttons">
      @if (isEditable()) {
        <p-button [label]="'SaveDraft' | translateWF: 'Save draft'" icon="pi pi-save" variant="outlined" (click)="save()" [disabled]="saving()" />
        <p-button [label]="'CashFunding_Receive' | translateWF: 'Post'" icon="pi pi-check" severity="success" (click)="post()" [disabled]="saving()" />
      }
      <p-button [label]="'Back' | translateWF: 'Back'" icon="pi pi-arrow-left" variant="outlined" (click)="back()" />
    </div>
  </header>

  <div class="content">
    <div class="grid-12 gap-[1.5rem]">
      <!-- Left column ───────────────────────────────────────────────────────── -->
      <div class="span-12-12-9 col">
        <div class="card">
          <h2 class="card__title">{{ 'CashFunding_Section' | translateWF: 'Funding' }}</h2>

          @if (isEditable()) {
            <!-- New / Draft: editable -->
            <form [formGroup]="form" class="grid-12">
              @if (isNew()) {
                <div class="span-12">
                  <div class="form-box">
                    <label for="cashRequest">{{ 'CashFunding_CashRequest' | translateWF: 'Cash request' }} <span class="text-red-500">*</span></label>
                    <p-select
                      id="cashRequest"
                      formControlName="cashRequest"
                      [options]="fundableOptions()"
                      optionLabel="label"
                      optionValue="value"
                      [filter]="true"
                      filterBy="label"
                      [showClear]="true"
                      appendTo="body"
                      styleClass="w-full"
                      [placeholder]="'CashFunding_SelectRequest' | translateWF: 'Select an approved request'"
                      (onChange)="onCashRequestChange($event.value)"
                      [ngClass]="{ 'ng-invalid ng-dirty': form.controls.cashRequest.invalid && form.controls.cashRequest.touched }"
                    />
                    <small class="text-surface-500">{{ 'CashFunding_CashRequest_Hint' | translateWF: 'Selecting a request loads its breakdown and currency below.' }}</small>
                  </div>
                </div>
              } @else {
                <div class="span-12-6">
                  <div class="form-box">
                    <label>{{ 'CashFunding_Funder' | translateWF: 'Funder' }}</label>
                    <span>{{ funding()?.funderName || '—' }}</span>
                  </div>
                </div>
                <div class="span-12-6">
                  <div class="form-box">
                    <label>{{ 'CashFunding_CashRequest' | translateWF: 'Cash request' }}</label>
                    <span class="font-mono">{{ funding()?.cashRequestNumber || '—' }}</span>
                  </div>
                </div>
              }

              <div class="span-12-4">
                <div class="form-box">
                  <label for="fundingDate">{{ 'CashFunding_Date' | translateWF: 'Funding date' }} <span class="text-red-500">*</span></label>
                  <p-datepicker inputId="fundingDate" formControlName="fundingDate" appendTo="body" styleClass="w-full" [showIcon]="true" />
                </div>
              </div>
              <div class="span-12-4">
                <div class="form-box">
                  <label>{{ 'CashFunding_Currency' | translateWF: 'Currency' }}</label>
                  <input pInputText [value]="currencyLabel(currency())" readonly class="w-full" />
                  <small class="text-surface-500">{{ 'CashFunding_Currency_Hint' | translateWF: 'From the selected cash request' }}</small>
                </div>
              </div>
              <div class="span-12-4">
                <div class="form-box">
                  <label for="amount">{{ 'CashFunding_Amount' | translateWF: 'Funding amount' }} <span class="text-red-500">*</span></label>
                  <p-inputNumber
                    inputId="amount"
                    formControlName="amount"
                    mode="decimal"
                    [minFractionDigits]="2"
                    [maxFractionDigits]="2"
                    [min]="0"
                    styleClass="w-full"
                    [suffix]="' ' + currencyLabel(currency())"
                  />
                  <small class="text-surface-500">{{ 'CashFunding_Amount_Hint' | translateWF: 'Defaults to the request total — adjust if the funded amount differs.' }}</small>
                </div>
              </div>
            </form>
          } @else if (funding()) {
            @if (funding(); as f) {
              <!-- Posted / Cancelled: read-only -->
              <section class="grid-12">
                <div class="span-12-4">
                  <div class="form-box">
                    <label>{{ 'CashFunding_Funder' | translateWF: 'Funder' }}</label>
                    <span>{{ f.funderName || '—' }}</span>
                  </div>
                </div>
                <div class="span-12-4">
                  <div class="form-box">
                    <label>{{ 'CashFunding_CashRequest' | translateWF: 'Cash request' }}</label>
                    <span class="font-mono">{{ f.cashRequestNumber || '—' }}</span>
                  </div>
                </div>
                <div class="span-12-4">
                  <div class="form-box">
                    <label>{{ 'CashFunding_Date' | translateWF: 'Funding date' }}</label>
                    <span>{{ f.fundingDate | date: 'dd/MM/yyyy' }}</span>
                  </div>
                </div>

                <div class="span-12-4">
                  <div class="form-box">
                    <label>{{ 'CashFunding_Currency' | translateWF: 'Currency' }}</label>
                    <span>{{ currencyLabel(f.currency) }}</span>
                  </div>
                </div>
              </section>
            }
          }
        </div>

        <!-- Bank, reference, charges & documents (shared component) -->
        <app-bank-charges-section
          [title]="'CashFunding_Section_Bank' | translateWF: 'Account, reference, charges & documents'"
          [bankAccountControl]="form.controls.bankAccount"
          [referenceControl]="form.controls.bankReference"
          [bankingFeeControl]="form.controls.bankingFee"
          [securityFeeControl]="form.controls.securityFee"
          [bankAccountOptions]="bankAccountOptions()"
          [currencyLabel]="currencyLabel(currency())"
          [attachments]="attachments()"
          [canAttach]="!!funding() && isEditable()"
          [referencePlaceholder]="'e.g. TRF-FICO-77310'"
          (fileSelected)="onFileSelected($event)"
          (attachmentOpened)="openAttachment($event)"
        />

        <!-- Cash request breakdown -->
        @if (request(); as req) {
          <div class="card">
            <div class="card__header">
              <h2 class="card__title">{{ 'CashFunding_Breakdown' | translateWF: 'Cash request breakdown' }}</h2>
              @if (isNew()) {
                <p-tag severity="success" [value]="'Approved' | translateWF: 'Approved'" />
              }
            </div>
            <div class="breakdown-meta">
              <div>
                <span class="meta-label">{{ 'CashRequest_Week' | translateWF: 'Week' }}</span>
                <span>{{ req.weekStart | date: 'dd/MM/yyyy' }} – {{ req.weekEnd | date: 'dd/MM/yyyy' }}</span>
              </div>
            </div>
            <app-cash-request-breakdown [lines]="req.lines" [currencyLabel]="currencyLabel(currency())" [totalLabel]="'CashFunding_RequestTotal' | translateWF: 'Request total'" />
          </div>
        }
      </div>

      <!-- Right column: funding summary (shared component) ──────────────────────── -->
      <div class="span-12-12-3 col">
        <app-settlement-summary
          [title]="'CashFunding_Summary' | translateWF: 'Funding summary'"
          [hint]="'CashFunding_Summary_Hint' | translateWF: 'Net is the funding amount minus banking and security fees.'"
          [baseLabel]="'CashFunding_Amount' | translateWF: 'Funding amount'"
          [baseAmount]="amountValue()"
          [extraRows]="request() ? summaryExtraRows() : []"
          [bankingFeeLabel]="'CashFunding_TransferFee' | translateWF: 'Banking fee'"
          [securityFeeLabel]="'CashFunding_SecurityTax' | translateWF: 'Security fee'"
          [bankingFee]="bankingFeeValue()"
          [securityFee]="securityFeeValue()"
          [feesReduceNet]="true"
          [netLabel]="'CashFunding_NetCash' | translateWF: 'Net cash received'"
          [net]="netCashReceived()"
          [currencyLabel]="currencyLabel(currency())"
        />

        <!-- Accounting (#5024 #5032). Replaces the bare journal entry number that used to sit among the
             posted-details fields — the number now heads the GL impact box itself. -->
        @if (showAccounting()) {
          <p-panel [header]="'CashFunding_Section_Accounting' | translateWF: 'Accounting'">
            <p-card>
              <div class="summary-rows">
                <div class="summary-rows__row row-start-center">
                  <span>{{ 'CashFunding_JournalEntry' | translateWF: 'Journal entry' }}</span>
                  <span class="ml-auto font-mono">{{ journalEntryDocumentNumber() }}</span>
                </div>
              </div>

              <span class="block mt-3 mb-2 text-[0.7rem] font-semibold uppercase tracking-wide text-surface-500">
                {{ 'ApDocument_GlImpact' | translateWF: 'GL impact' }}
              </span>
              <app-journal-entry-summary [references]="journalEntryReferences()" [currencyLabel]="currencyLabel(currency())" />
            </p-card>
          </p-panel>
        }
      </div>
    </div>
  </div>
</main>
